import { serviceHelper } from './service-helper';
import { searchClientWithProxy } from '../utils/algolia/createInstantSearchProxy';
import type { SearchClient } from 'instantsearch.js/es/types';

const onlineSearchApiUrl = serviceHelper.onlineSearchApiUrl ?? '';

/**
 * When searchProvider === 'google', the actual search data flows through
 * GoogleSearchClient's single-endpoint `search()` method via custom hooks
 * (useGoogleSuggestions, useGoogleSearch, etc.).
 *
 * However, SearchBar.js is always wrapped in <InstantSearch> which requires
 * a searchClient with the multi-request `search(requests)` interface.
 * This adapter returns empty results since InstantSearch isn't used for
 * rendering Google results — it only satisfies useSearchBox's internal calls.
 */
const createGoogleInstantSearchAdapter = (): SearchClient => {
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      '[searchClient] Using Google InstantSearch adapter — all Algolia search() calls return empty results. ' +
        'Do not add Algolia widgets that depend on real search data inside the Google provider path.'
    );
  }

  const emptyResult = {
    hits: [],
    nbHits: 0,
    page: 0,
    nbPages: 0,
    hitsPerPage: 0,
    exhaustiveNbHits: true,
    query: '',
    params: '',
    processingTimeMS: 0,
  };

  return {
    search: async (requests: Array<any>) => ({
      results: requests.map(() => ({ ...emptyResult })),
    }),
  };
};

export const createSearchClient = (apiKey: string): SearchClient => {
  const searchProvider = window.Jbhifi?.searchProvider || 'algolia';

  if (searchProvider === 'google') {
    if (!onlineSearchApiUrl) {
      throw new Error('Online search API URL not configured for Google search provider');
    }
    return createGoogleInstantSearchAdapter();
  }

  return searchClientWithProxy({ apiKey });
};
